<template>
  <div>
    <div class="calculator-container">
      <h1 class="h3">
        P-Value Calculator
      </h1>
      <form @submit.prevent="calculations">
        <div class="dropdowns-inputs margin-bottom">
          <div class="inputs-tooltip">
            <div class="label-tooltip">
              <label>
                Distribution
              </label>
              <div class="icon-info-tooltip distribution">
                <IconInfo />
                <Tooltip>
                  <span>Choose the distribution type.</span>
                </Tooltip>
              </div>
            </div>
            <Dropdown
              calculator
              class="small-dropdown"
              :selected="selectedDistribution"
              :radio-buttons="{ 'standardNormal': 'Standard Normal', 'studentsT': 'Student’s T', 'chiSquared': 'Chi-Square', 'f': 'F' }"
              @change="currentSelectedDistribution"
            />
          </div>
          <div class="inputs-tooltip">
            <div class="label-tooltip">
              <label v-if="selectedDistribution === 'chiSquared'" class="x-squared"><span class="chi">&chi;</span><span class="two">2</span><span class="score">-score</span></label>
              <label v-else>
                {{ currentScoreName }}
              </label>
              <div class="icon-info-tooltip score">
                <IconInfo />
                <Tooltip>
                  <span>Provide the test's statistic value.</span>
                </Tooltip>
              </div>
            </div>
            <input
              v-model="currentScoreOriginal" type="text"
              :class="{ 'invalid-value': currentScoreAlert }"
              maxlength="7"
            >
            <div v-if="currentScoreAlert" class="error">
              {{ currentScoreAlert }}
            </div>
          </div>
        </div>
        <div v-if="selectedDistribution === 'f'" class="dropdowns-inputs margin-bottom">
          <div class="inputs-tooltip">
            <div class="label-tooltip">
              <label>
                Numerator Degrees of Freedom
              </label>
              <div class="icon-info-tooltip numerator">
                <IconInfo />
                <Tooltip>
                  <span>Provide the numerator degrees of freedom.</span>
                </Tooltip>
              </div>
            </div>
            <input
              v-model="numerator" type="text"
              :class="{ 'invalid-value': numeratorAlert }"
            >
            <div v-if="numeratorAlert" class="error">
              {{ numeratorAlert }}
            </div>
          </div>
          <div class="inputs-tooltip">
            <div class="label-tooltip">
              <label>
                Denominator Degrees of Freedom
              </label>
              <div class="icon-info-tooltip denominator">
                <IconInfo />
                <Tooltip>
                  <span>Provide the denominator degrees of freedom.</span>
                </Tooltip>
              </div>
            </div>
            <input
              v-model="denominator" type="text"
              :class="{ 'invalid-value': denominatorAlert }"
            >
            <div v-if="denominatorAlert" class="error">
              {{ denominatorAlert }}
            </div>
          </div>
        </div>
        <div
          v-if="selectedDistribution === 'standardNormal' || selectedDistribution === 'f'"
          class="dropdowns-inputs"
        >
          <div class="inputs-tooltip">
            <div class="label-tooltip">
              <label>
                Hypothesis Test
              </label>
              <div class="icon-info-tooltip hypothesis">
                <IconInfo />
                <Tooltip>
                  <span>Select the appropriate alternative hypothesis.</span>
                </Tooltip>
              </div>
            </div>
            <Dropdown
              calculator
              class="small-dropdown"
              :selected="selectedHypothesisTest"
              :radio-buttons="{ 'rightTailed': 'Right-tailed', 'leftTailed': 'Left-tailed', 'twoTailed': 'Two-tailed' }"
              @change="currentSelectedHypothesisTest"
            />
          </div>
          <div class="inputs-tooltip">
            <div class="label-tooltip">
              <label>
                Significance Level
              </label>
              <div class="icon-info-tooltip significance">
                <IconInfo />
                <Tooltip>
                  <span>Provide a positive numerical significance level.</span>
                </Tooltip>
              </div>
            </div>
            <div class="input-dropdown">
              <input
                v-model="selectedSignificanceLevel" type="text"
                :class="{ 'invalid-value': significanceLevelAlert, 'dropdown-opened': dropdownOpened }"
              >
              <Dropdown
                calculator
                calculator-input-dropdown-combination
                class="small-dropdown"
                :class="{ 'add-background': arrowHover, 'invalid-value': significanceLevelAlert }"
                :selected="selectedSignificanceLevel"
                :radio-buttons="{ '0.01': '0.01', '0.05': '0.05', '0.10': '0.10' }"
                @change="currentSelectedSignificanceLevel"
                @arrowHover="arrowHover = true"
                @arrowLeave="arrowHover = false"
                @arrowClicked="dropdownOpened = !dropdownOpened"
                @dropdownClosed="dropdownOpened = false"
              />
            </div>
            <div v-if="significanceLevelAlert" class="error">
              {{ significanceLevelAlert }}
            </div>
          </div>
        </div>
        <div v-if="selectedDistribution === 'chiSquared' || selectedDistribution === 'studentsT'" class="dropdowns-inputs">
          <div class="inputs-tooltip">
            <div class="label-tooltip">
              <label>
                Degrees Of Freedom
              </label>
              <div class="icon-info-tooltip degrees-of-freedom">
                <IconInfo />
                <Tooltip>
                  <span>Provide the degrees of freedom.</span>
                </Tooltip>
              </div>
            </div>
            <input
              v-model="degreesOfFreedom" type="text"
              :class="{ 'invalid-value': degreesOfFreedomAlert }"
            >
            <div v-if="degreesOfFreedomAlert" class="error">
              {{ degreesOfFreedomAlert }}
            </div>
          </div>
          <div class="inputs-tooltip">
            <div class="label-tooltip">
              <label>
                Hypothesis Test
              </label>
              <div class="icon-info-tooltip hypothesis">
                <IconInfo />
                <Tooltip>
                  <span>Select the appropriate alternative hypothesis.</span>
                </Tooltip>
              </div>
            </div>
            <Dropdown
              calculator
              class="small-dropdown"
              :selected="selectedHypothesisTest"
              :radio-buttons="{ 'rightTailed': 'Right-tailed', 'leftTailed': 'Left-tailed', 'twoTailed': 'Two-tailed' }"
              @change="currentSelectedHypothesisTest"
            />
          </div>
        </div>
        <div class="dropdowns-inputs third">
          <div
            v-if="selectedDistribution === 'chiSquared' || selectedDistribution === 'studentsT'"
            class="inputs-tooltip"
          >
            <div class="label-tooltip">
              <label>
                Significance Level
              </label>
              <div class="icon-info-tooltip significance">
                <IconInfo />
                <Tooltip>
                  <span>Provide a positive numerical significance level.</span>
                </Tooltip>
              </div>
            </div>
            <div class="input-dropdown">
              <input
                v-model="selectedSignificanceLevel" type="text"
                :class="{ 'invalid-value': significanceLevelAlert, 'dropdown-opened': dropdownOpened }"
              >
              <Dropdown
                calculator
                calculator-input-dropdown-combination
                class="small-dropdown"
                :class="{ 'add-background': arrowHover, 'invalid-value': significanceLevelAlert }"
                :selected="selectedSignificanceLevel"
                :radio-buttons="{ '0.01': '0.01', '0.05': '0.05', '0.10': '0.10' }"
                @change="currentSelectedSignificanceLevel"
                @arrowHover="arrowHover = true"
                @arrowLeave="arrowHover = false"
                @arrowClicked="dropdownOpened = !dropdownOpened"
                @dropdownClosed="dropdownOpened = false"
              />
            </div>
            <div v-if="significanceLevelAlert" class="error">
              {{ significanceLevelAlert }}
            </div>
          </div>
          <div
            class="decimal-places"
            :class="{ 'not-single': selectedDistribution === 'chiSquared' || selectedDistribution === 'studentsT' }"
          >
            <div class="label-tooltip">
              <label>
                Decimal Places
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip>
                  <span>Choose the number of decimal places.</span>
                </Tooltip>
              </div>
            </div>
            <Dropdown
              calculator
              calculator-decimal-places
              class="small-dropdown"
              :selected="selectedDecimalPlaces"
              :radio-buttons="{ 1: '1', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9' }"
              @change="currentSelectedDecimalPlace"
            />
          </div>
        </div>
        <div class="buttons">
          <button class="btn-primary calculate">
            Calculate
          </button><button
            class="btn-primary-light reset"
            @click="reset"
          >
            Reset
          </button>
          <button class="load-example btn-link-accent" @click="loadExample">
            Load Example
          </button>
        </div>
      </form>
    </div>

    <div v-show="pValue" id="interpretation" class="interpretation-container">
      <h4 class="interpretation-heading">
        Interpretation:
      </h4>
      <p class="colored-texts-p-value">
        The p-value is {{ pValue }}
      </p>
      <p class="colored-texts-p-value">
        The result is <span v-if="significantOrNot[0]">not </span>significant at p <span v-html="significantOrNot[1]" /> {{ selectedSignificanceLevel }}
      </p>
      <p v-if="significantOrNot[0]">
        At the specified level of significance (α = {{ selectedSignificanceLevel }}), the calculated p-value suggests that the result is <span class="bolded-texts">not statistically significant</span>. This implies that there is insufficient evidence to reject the null hypothesis.
      </p>
      <p v-else>
        At the specified level of significance (α = {{ selectedSignificanceLevel }}), the calculated p-value suggests that the result is <span class="bolded-texts">statistically significant</span>. This implies that there is sufficient evidence to reject the null hypothesis and support the alternative hypothesis.
      </p>
    </div>

    <div v-if="pValue" class="python-r-codes-container">
      <div class="python-r-codes python">
        <span class="img-text"><img alt="Python icon" src="../../../../assets/images/icon-python-calc.png"> <h4 class="h6">Your Result in Python:</h4></span>
        <p class="codes-info">
          The following Python code should provide p-value using the input data you entered.
        </p>
        <div class="codes-container">
          <div v-if="selectedDistribution === 'standardNormal'" class="codes">
            <pre
              v-if="selectedHypothesisTest === 'leftTailed'"
              class="language-python"
            >
            <code id="python">
from scipy.stats import norm

# Calculate the p-value

z_score = {{ currentScoreOriginal }}

p_value = norm.cdf(z_score)

# Print the p-value

print("p-value:", p_value)</code>
  </pre>
            <pre
              v-else-if="selectedHypothesisTest === 'rightTailed'"
              class="language-python"
            >
            <code id="python">
from scipy.stats import norm

# Calculate the p-value

z_score = {{ currentScoreOriginal }}

p_value = 1 - norm.cdf(z_score)

# Print the p-value

print("p-value:", p_value)</code>
  </pre>
            <pre
              v-else
              class="language-python"
            >
            <code id="python">
from scipy.stats import norm

# Calculate the p-value

z_score = {{ currentScoreOriginal }}

p_value = 2 * (1 - norm.cdf(abs(z_score)))

# Print the p-value

print("p-value:", p_value)</code>
  </pre>
          </div>
          <div v-else-if="selectedDistribution === 'studentsT'" class="codes">
            <pre
              v-if="selectedHypothesisTest === 'leftTailed'"
              class="language-python"
            >
            <code id="python">
from scipy.stats import t

# Calculate the p-value for a leftTailed test

t_statistic = {{ currentScoreOriginal }}

# Specify the appropriate degrees of freedom

degrees_of_freedom = {{ degreesOfFreedom }}

p_value = t.cdf(t_statistic, df=degrees_of_freedom)

# Print the p-value

print("Left-tailed p-value:", p_value)</code>
  </pre>
            <pre
              v-else-if="selectedHypothesisTest === 'rightTailed'"
              class="language-python"
            >
            <code id="python">
from scipy.stats import t

# Calculate the p-value for a {{ selectedHypothesisTest }} test

t_statistic = {{ currentScoreOriginal }}

# Specify the appropriate degrees of freedom

degrees_of_freedom = {{ degreesOfFreedom }}

p_value = 1 - t.cdf(t_statistic, df=degrees_of_freedom)

# Print the p-value

print("Right-tailed p-value:", p_value)</code>
  </pre>
            <pre
              v-else
              class="language-python"
            >
            <code id="python">
from scipy.stats import t

# Calculate the p-value for a twoTailed test

t_statistic = {{ currentScoreOriginal }}

# Specify the appropriate degrees of freedom

degrees_of_freedom = {{ degreesOfFreedom }}

p_value = 2 * (1 - t.cdf(abs(t_statistic), df=degrees_of_freedom))

# Print the p-value

print("Two-tailed p-value:", p_value)</code>
  </pre>
          </div>
          <div v-else-if="selectedDistribution === 'chiSquared'" class="codes">
            <pre
              v-if="selectedHypothesisTest === 'leftTailed'"
              class="language-python"
            >
            <code id="python">
from scipy.stats import chi2

# Calculate the p-value for a leftTailed test

chi2_statistic = {{ currentScoreOriginal }}

# Specify the appropriate degrees of freedom

degrees_of_freedom = {{ degreesOfFreedom }}

p_value = chi2.cdf(chi2_statistic, df=degrees_of_freedom)

# Print the p-value

print("Left-tailed p-value:", p_value)</code>
  </pre>
            <pre
              v-else-if="selectedHypothesisTest === 'rightTailed'"
              class="language-python"
            >
            <code id="python">
from scipy.stats import chi2

# Calculate the p-value for a {{ selectedHypothesisTest }} test

chi2_statistic = {{ currentScoreOriginal }}

# Specify the appropriate degrees of freedom

degrees_of_freedom = {{ degreesOfFreedom }}

p_value = 1 - chi2.cdf(chi2_statistic, df=degrees_of_freedom)

# Print the p-value

print("Right-tailed p-value:", p_value)</code>
  </pre>
            <pre
              v-else
              class="language-python"
            >
            <code id="python">
from scipy.stats import chi2

# Calculate the p-value for a two-tailed test

chi2_statistic = {{ currentScoreOriginal }}

# Specify the appropriate degrees of freedom

degrees_of_freedom = {{ degreesOfFreedom }}

# Calculate the right-tail probability

right_tail_prob = chi2.sf(chi2_statistic, df=degrees_of_freedom)

# Calculate the left-tail probability

left_tail_prob = chi2.cdf(chi2_statistic, df=degrees_of_freedom)

# Calculate the two-tailed p-value by summing the probabilities

p_value = 2 * min(left_tail_prob, right_tail_prob)

# Print the p-value

print("Two-tailed p-value:", p_value)</code>
  </pre>
          </div>
          <div v-else class="codes">
            <pre
              v-if="selectedHypothesisTest === 'leftTailed'"
              class="language-python"
            >
            <code id="python">
import scipy.stats as stats

# Specify the appropriate degrees of freedom

numerator_df = {{ numerator }}

denominator_df = {{ denominator }}

# Specify the F-statistic

f_statistic = {{ currentScoreOriginal }}

# Calculate the p-value for a leftTailed test

p_value = stats.f.cdf(f_statistic, numerator_df, denominator_df)

# Print the p-value

print("Left-tailed p-value:", p_value)</code>
  </pre>
            <pre
              v-else-if="selectedHypothesisTest === 'rightTailed'"
              class="language-python"
            >
            <code id="python">
import scipy.stats as stats

# Specify the appropriate degrees of freedom

numerator_df = {{ numerator }}

denominator_df = {{ denominator }}

# Specify the F-statistic

f_statistic = {{ currentScoreOriginal }}

# Calculate the p-value for a {{ selectedHypothesisTest }} test

p_value = 1 - stats.f.cdf(f_statistic, numerator_df, denominator_df)

# Print the p-value

print("Right-tailed p-value:", p_value)</code>
  </pre>
            <pre
              v-else
              class="language-python"
            >
            <code id="python">
import scipy.stats as stats

# Specify the appropriate degrees of freedom

numerator_df = {{ numerator }}

denominator_df = {{ denominator }}

# Specify the F-statistic

f_statistic = {{ currentScoreOriginal }}

# Calculate the p-value for a twoTailed test

p_value = 2 * (1 - stats.f.cdf(f_statistic, numerator_df, denominator_df))

# Print the p-value

print("Two-tailed p-value:", p_value)</code>
  </pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedPython === 'Copied' }" @click="copyCode('python')">
          {{ copyCodeOrCopiedPython }}
        </button>
      </div>
      <div class="python-r-codes">
        <span class="img-text"><img alt="Icon for R language" src="../../../../assets/images/icon-r-calc.png"> <h4 class="h6">Your Result in R:</h4></span>
        <p class="codes-info">
          The following R code should provide p-value using the input data you entered.
        </p>
        <div class="codes-container">
          <div v-if="selectedDistribution === 'standardNormal'" class="codes">
            <pre
              v-if="selectedHypothesisTest === 'leftTailed'"
              class="language-r"
            >
            <code id="r">
# Calculate the p-value for a leftTailed test

z_score &lt;- {{ currentScoreOriginal }}

p_value &lt;- pnorm(z_score, lower.tail = TRUE)

# Print the p-value

cat("Left-tailed p-value:", p_value, "\n")</code>
  </pre>
            <pre
              v-else-if="selectedHypothesisTest === 'rightTailed'"
              class="language-r"
            >
            <code id="r">
# Calculate the p-value for a {{ selectedHypothesisTest }} test

z_score &lt;- {{ currentScoreOriginal }}

p_value &lt;- pnorm(z_score, lower.tail = FALSE)

# Print the p-value

cat("Right-tailed p-value:", p_value, "\n")</code>
  </pre>
            <pre
              v-else
              class="language-r"
            >
            <code id="r">
# Calculate the p-value for a twoTailed test

z_score &lt;- {{ currentScoreOriginal }}

p_value &lt;- 2 * pnorm(-abs(z_score))

# Print the p-value

cat("Two-tailed p-value:", p_value, "\n")</code>
  </pre>
          </div>
          <div v-else-if="selectedDistribution === 'studentsT'" class="codes">
            <pre
              v-if="selectedHypothesisTest === 'leftTailed'"
              class="language-r"
            >
            <code id="r">
# Calculate the p-value for a leftTailed test

t_statistic &lt;- {{ currentScoreOriginal }}

# Specify the appropriate degrees of freedom

degrees_of_freedom &lt;- {{ degreesOfFreedom }}

p_value &lt;- pt(t_statistic, df = degrees_of_freedom)

# Print the p-value

cat("Left-tailed p-value:", p_value, "\n")</code>
  </pre>
            <pre
              v-else-if="selectedHypothesisTest === 'rightTailed'"
              class="language-r"
            >
            <code id="r">
# Calculate the p-value for a {{ selectedHypothesisTest }} test

t_statistic &lt;- {{ currentScoreOriginal }}

# Specify the appropriate degrees of freedom

degrees_of_freedom &lt;- {{ degreesOfFreedom }}

p_value &lt;- 1 - pt(t_statistic, df = degrees_of_freedom)

# Print the p-value

cat("Right-tailed p-value:", p_value, "\n")</code>
  </pre>
            <pre
              v-else
              class="language-r"
            >
            <code id="r">
# Calculate the p-value for a twoTailed test

t_statistic &lt;- {{ currentScoreOriginal }}

# Specify the appropriate degrees of freedom

degrees_of_freedom &lt;- {{ degreesOfFreedom }}

p_value &lt;- 2 * (1 - pt(abs(t_statistic), df = degrees_of_freedom))

# Print the p-value

cat("Two-tailed p-value:", p_value, "\n")</code>
  </pre>
          </div>
          <div v-else-if="selectedDistribution === 'chiSquared'" class="codes">
            <pre
              v-if="selectedHypothesisTest === 'leftTailed'"
              class="language-r"
            >
            <code id="r">
# Calculate the p-value for a leftTailed test

chi2_statistic &lt;- {{ currentScoreOriginal }}

# Specify the appropriate degrees of freedom

degrees_of_freedom &lt;- {{ degreesOfFreedom }}

p_value &lt;- pchisq(chi2_statistic, df = degrees_of_freedom, lower.tail = TRUE)

# Print the p-value

print(paste("Left-tailed p-value:", p_value))</code>
  </pre>
            <pre
              v-else-if="selectedHypothesisTest === 'rightTailed'"
              class="language-r"
            >
            <code id="r">
# Calculate the p-value for a {{ selectedHypothesisTest }} test

chi2_statistic &lt;- {{ currentScoreOriginal }}

# Specify the appropriate degrees of freedom

degrees_of_freedom &lt;- {{ degreesOfFreedom }}

p_value &lt;- pchisq(chi2_statistic, df = degrees_of_freedom, lower.tail = FALSE)

# Print the p-value

print(paste("Right-tailed p-value:", p_value))</code>
  </pre>
            <pre
              v-else
              class="language-r"
            >
            <code id="r">
# Set the chi-square statistic

chi2_statistic &lt;- {{ currentScoreOriginal }}

# Set the degrees of freedom

degrees_of_freedom &lt;- {{ degreesOfFreedom }}

# Calculate the right-tail probability

right_tail_prob &lt;- 1 - pchisq(chi2_statistic, df = degrees_of_freedom)

# Calculate the left-tail probability

left_tail_prob &lt;- pchisq(chi2_statistic, df = degrees_of_freedom)

# Calculate the two-tailed p-value by summing the probabilities

p_value &lt;- 2 * min(left_tail_prob, right_tail_prob)

# Print the p-value

print(paste("Two-tailed p-value:", p_value))</code>
  </pre>
          </div>
          <div v-else class="codes">
            <pre
              v-if="selectedHypothesisTest === 'leftTailed'"
              class="language-r"
            >
            <code id="r">
# Specify the appropriate degrees of freedom

numerator_df &lt;- {{ numerator }}

denominator_df &lt;- {{ denominator }}

# Specify the F-statistic

f_statistic &lt;- {{ currentScoreOriginal }}

# Calculate the p-value for a leftTailed test

p_value &lt;- pf(f_statistic, numerator_df, denominator_df, lower.tail = TRUE)

# Print the p-value

cat("Left-tailed p-value:", p_value, "\n")</code>
  </pre>
            <pre
              v-else-if="selectedHypothesisTest === 'rightTailed'"
              class="language-r"
            >
            <code id="r">
# Specify the appropriate degrees of freedom

numerator_df &lt;- {{ numerator }}

denominator_df &lt;- {{ denominator }}

# Specify the F-statistic

f_statistic &lt;- {{ currentScoreOriginal }}

# Calculate the p-value for a {{ selectedHypothesisTest }} test

p_value &lt;- 1 - pf(f_statistic, numerator_df, denominator_df, lower.tail = TRUE)

# Print the p-value

cat("Right-tailed p-value:", p_value, "\n")</code>
  </pre>
            <pre
              v-else
              class="language-r"
            >
            <code id="r">
# Specify the appropriate degrees of freedom

numerator_df &lt;- {{ numerator }}

denominator_df &lt;- {{ denominator }}

# Specify the F-statistic

f_statistic &lt;- {{ currentScoreOriginal }}

# Calculate the p-value for a twoTailed test

p_value &lt;- 2 * (1 - pf(f_statistic, numerator_df, denominator_df, lower.tail = TRUE))

# Print the p-value

cat("Two-tailed p-value:", p_value, "\n")</code>
  </pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedR === 'Copied' }" @click="copyCode('r')">
          {{ copyCodeOrCopiedR }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import calculatorsMainMixin from '@itbpbg/lms-components/src/mixins/calculators/main';
import Dropdown from '@itbpbg/lms-components/src/components/Dropdown.vue';
import Tooltip from '@itbpbg/lms-components/src/components/Tooltip.vue';
import IconInfo from '@itbpbg/lms-components/src/assets/svg/icon-info.svg?vue-component';
import hljs from 'highlight.js';
import 'highlight.js/styles/github.css';
import jStat from 'jstat';

export default {
  components: {
    Dropdown,
    Tooltip,
    IconInfo,
  },
  mixins: [calculatorsMainMixin],
  data() {
    return {
      pValue: '',
      selectedDistribution: 'standardNormal',
      selectedHypothesisTest: 'twoTailed',
      selectedSignificanceLevel: '0.05',
      currentScoreName: 'Z-score',
      currentScoreOriginal: '',
      currentScore: '',
      numerator: '',
      denominator: '',
      degreesOfFreedom: '',
      selectedDecimalPlaces: '4',
      arrowHover: false,
      dropdownOpened: false,
      distributionAlert: '',
      currentScoreAlert: '',
      numeratorAlert: '',
      denominatorAlert: '',
      significanceLevelAlert: '',
      degreesOfFreedomAlert: '',
      copyCodeOrCopiedPython: 'Copy Code',
      copyCodeOrCopiedR: 'Copy Code',
    };
  },
  computed: {
    hideResultVariablesArr() {
      return [
        this.currentScoreOriginal,
        this.selectedHypothesisTest,
        this.selectedSignificanceLevel,
        this.degreesOfFreedom,
        this.numerator,
        this.denominator,
        this.selectedDecimalPlaces,
      ];
    },
    significantOrNot() {
      return [
        this.selectedSignificanceLevel < this.pValue,
        this.selectedSignificanceLevel >= this.pValue ? '&lt;' : '&gt;',
      ];
    },
  },
  watch: {
    selectedDistribution() {
      this.defaultingInputs();
    },
    hideResultVariablesArr() {
      this.hideResults();
    },
  },
  methods: {
    scrollToSection() {
      this.$nextTick(() => {
        let yOffset;
        if (this.$store.state.layout === 'desktop') {
          yOffset = -90;
        } else {
          yOffset = this.$config.project === 'lms' ? -64 : -68;
        }

        const section = 'interpretation';
        const element = document.querySelector(`#${section}`);
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        setTimeout(() => {
          this.clickedSection = section;
          window.scrollTo({ top: y, behavior: 'smooth' });

          setTimeout(() => {
            this.clickedSection = null;
          }, 500);
        }, 200);
      });
    },
    copyCode(id) {
      if (id.split('-')[0] === 'python') {
        this.copyCodeOrCopiedPython = 'Copied';
        setTimeout(() => {
          this.copyCodeOrCopiedPython = 'Copy Code';
        }, 1000);
      } else {
        this.copyCodeOrCopiedR = 'Copied';
        setTimeout(() => {
          this.copyCodeOrCopiedR = 'Copy Code';
        }, 1000);
      }

      const currentText = document.getElementById(id).innerText;
      this.$copy(currentText);
    },
    reset(e) {
      this.defaultingInputs();
      this.selectedDistribution = 'standardNormal';

      e.preventDefault();
    },
    loadExample(e) {
      this.defaultingInputs();

      if (this.selectedDistribution === 'standardNormal') {
        this.currentScoreOriginal = '3';
      } else if (['studentsT', 'chiSquared'].includes(this.selectedDistribution)) {
        this.currentScoreOriginal = '3';
        this.degreesOfFreedom = '10';
      } else if (this.selectedDistribution === 'f') {
        this.currentScoreOriginal = '3';
        this.numerator = '10';
        this.denominator = '5';
      }

      e.preventDefault();
    },
    defaultingInputs() {
      this.pValue = '';
      this.selectedHypothesisTest = 'twoTailed';
      this.selectedSignificanceLevel = '0.05';
      this.currentScoreOriginal = '';
      this.degreesOfFreedom = '';
      this.numerator = '';
      this.denominator = '';
      this.selectedDecimalPlaces = '4';
    },
    hideResults() {
      this.pValue = '';
      this.removeAlerts();
    },
    removeAlerts() {
      const alertsArray = [
        'distributionAlert',
        'significanceLevelAlert',
        'currentScoreAlert',
        'numeratorAlert',
        'denominatorAlert',
        'degreesOfFreedomAlert',
      ];

      for (let i = 0; i < alertsArray.length; i += 1) {
        this[alertsArray[i]] = false;
      }
    },
    currentSelectedDistribution(selectedDistribution) {
      this.selectedDistribution = selectedDistribution;

      if (selectedDistribution === 'standardNormal') {
        this.currentScoreName = 'Z-score';
      } else if (selectedDistribution === 'studentsT') {
        this.currentScoreName = 'T-score';
      } else {
        this.currentScoreName = 'F-score';
      }
    },
    currentSelectedHypothesisTest(selectedHypothesisTest) {
      this.selectedHypothesisTest = selectedHypothesisTest;
    },
    currentSelectedSignificanceLevel(selectedSignificanceLevel) {
      this.selectedSignificanceLevel = selectedSignificanceLevel;
    },
    inputValidation() {
      this.removeAlerts();
      let validationResult = true;
      this.currentScore = '';

      if (this.selectedSignificanceLevel.length > 4) {
        this.significanceLevelAlert = 'Number of decimal places is limited to 2.';
        validationResult = false;
      } else if (this.selectedSignificanceLevel < 0.01 || this.selectedSignificanceLevel > 1) {
        this.significanceLevelAlert = 'The number must be between 0 and 1.';
        validationResult = false;
      }

      if (Number.isNaN(Number(this.currentScoreOriginal))) {
        this.currentScore = this.currentScoreOriginal;
      } else if (this.currentScoreOriginal !== '') {
        if (['chiSquared', 'f'].includes(this.selectedDistribution) && this.currentScoreOriginal < 0) {
          const distributionName = this.selectedDistribution === 'chiSquared' ? 'chi-square score' : 'F-score';
          this.currentScoreAlert = `Please provide a positive ${distributionName}.`;
          validationResult = false;
        }

        this.currentScore = Math.abs(Number(this.currentScoreOriginal)).toString();
      }

      let inputValuesArray = [];
      let alertsArray = [];

      const inputsArr = ['currentScore', 'degreesOfFreedom', 'numerator', 'denominator', 'selectedSignificanceLevel'];
      for (let i = 0; i < inputsArr.length; i += 1) {
        this[inputsArr[i]] = this[inputsArr[i]].trim();
      }

      if (this.selectedDistribution === 'standardNormal') {
        alertsArray = ['currentScoreAlert', 'significanceLevelAlert'];
        inputValuesArray = [this.currentScore, this.selectedSignificanceLevel];
      } else if (['studentsT', 'chiSquared'].includes(this.selectedDistribution)) {
        alertsArray = ['currentScoreAlert', 'degreesOfFreedomAlert', 'significanceLevelAlert'];
        inputValuesArray = [this.currentScore, this.degreesOfFreedom, this.selectedSignificanceLevel];
      } else {
        alertsArray = ['currentScoreAlert', 'numeratorAlert', 'denominatorAlert', 'significanceLevelAlert'];
        inputValuesArray = [this.currentScore, this.numerator, this.denominator, this.selectedSignificanceLevel];
      }

      const invalidValuesArray = inputValuesArray.map((value) => ((value === '' || (Number.isNaN(Number(value)) || value < 0)) ? value : 'valid'));
      for (let i = 0; i < invalidValuesArray.length; i += 1) {
        if (invalidValuesArray[i] !== 'valid') {
          if (invalidValuesArray[i] === '') {
            this[alertsArray[i]] = 'Field required';
          } else {
            this[alertsArray[i]] = 'Invalid value';
          }

          validationResult = false;
        }
      }

      return validationResult;
    },
    calculations() {
      if (!this.inputValidation()) {
        return;
      }

      let currentTail = this.selectedHypothesisTest;

      if (['leftTailed', 'rightTailed'].includes(currentTail) && this.currentScoreOriginal < 0) {
        currentTail = currentTail === 'leftTailed' ? 'rightTailed' : 'leftTailed';
      }

      if (this.selectedDistribution === 'standardNormal') {
        this.pValue = jStat.normal.cdf(Number(this.currentScore), 0, 1);

        if (currentTail === 'rightTailed') {
          this.pValue = 1 - this.pValue;
        } else if (currentTail === 'twoTailed') {
          this.pValue = 2 * (1 - this.pValue);
        }
      } else if (this.selectedDistribution === 'studentsT') {
        this.pValue = jStat.ttest(Number(this.currentScore), Number(this.degreesOfFreedom) + 1);

        if (currentTail === 'rightTailed') {
          this.pValue /= 2;
        } else if (currentTail === 'leftTailed') {
          this.pValue = 1 - this.pValue / 2;
        }
      } else if (this.selectedDistribution === 'chiSquared') {
        this.pValue = jStat.chisquare.cdf(Number(this.currentScore), Number(this.degreesOfFreedom));

        if (currentTail === 'rightTailed') {
          this.pValue = 1 - this.pValue;
        } else if (currentTail === 'twoTailed') {
          this.pValue *= 2;
        }
        if (Number(this.currentScore) >= Number(this.degreesOfFreedom) && currentTail === 'twoTailed') {
          this.pValue = 2 - this.pValue;
        }
      } else if (this.selectedDistribution === 'f') {
        this.pValue = jStat.centralF.cdf(Number(this.currentScore), Number(this.numerator), Number(this.denominator));

        if (currentTail === 'rightTailed') {
          this.pValue = 1 - this.pValue;
        } else if (currentTail === 'twoTailed') {
          this.pValue = 2 * (1 - this.pValue);
        }
      }

      this.pValue = this.pValue.toFixed(this.selectedDecimalPlaces);

      this.$nextTick(() => {
        document.querySelectorAll('pre code:not(.hljs)').forEach((block) => {
          hljs.highlightElement(block);
        });
      });

      this.scrollToSection();
    },
  },
};
</script>

<style lang="scss" scoped>
.calculator-container {
  .dropdowns-inputs {
    display: flex;
    gap: var(--spacing-30);

    &.margin-bottom {
      margin-bottom: var(--spacing-30);
    }

    &.third {
      margin: var(--spacing-30) 0 var(--spacing-36);

      .decimal-places {
        margin: 0;

        &.not-single {
          width: 100%;
        }
      }
    }

    .inputs-tooltip {
      display: flex;
      flex-direction: column;

      input.invalid-value {
        border-color: #E04A4A;
      }

      .x-squared {
        position: relative;

        .chi {
          position: absolute;
          top: -2.5px;
        }

        .two {
          display: inline-block;
          font-size: 10px;
          position: absolute;
          top: -3px;
          left: 8px;
        }

        .score {
          padding-left: var(--spacing-10);
        }
      }

      .input-dropdown {
        display: flex;
        position: relative;

        .dropdown.small-dropdown {
          width: 32px;
          border-left: unset;
          position: unset;
          border-radius: 0 5px 5px 0;

          &.add-background {
            background: var(--main-light-shade-2);
            border: 1px solid rgba(0, 12, 31, 0.18);
          }

          &.open {
            border: 1px solid var(--main-dark);
            border-bottom: 0;
            background: var(--main-light-shade-2);
            border-radius: 0 5px 0 0;
          }

          &.invalid-value {
            border-color: #E04A4A;
          }
        }

        .dropdown::v-deep {
          height: 52px;
          border-radius: 0 5px 5px 0;
          border: 1px solid rgba(0, 12, 31, 0.18);

          label.active span {
            font-weight: 600;
          }

          .list {
            left: unset;
          }

          .list-head {
            width: 32px;
            height: 100%;
            padding: 0;
            justify-content: center;
            max-width: unset;

            svg {
              position: unset;
            }

            span {
              display: none;
            }
          }
        }

        input {
          border-radius: 5px 0 0 5px;
          border: 1px solid rgba(0, 12, 31, 0.18);
          border-right: unset;

          &.dropdown-opened {
            border-radius: 5px 0 0 0;
            border-bottom: 0;
          }
        }

        input:focus {
          border: 1px solid var(--main-dark);
        }

        input.invalid-value {
          border-color: #E04A4A;
        }
      }
    }
  }
}

.interpretation-container {
  background: var(--accent-light-1) !important;

  .colored-texts-p-value {
    font-weight: 600;
    color: var(--accent-shade-2);
  }
}

.python-r-codes-container {
  margin-top: var(--spacing-48);
  background: var(--main-light);
  border: 1px solid rgba(0, 12, 31, 0.18);
  border-radius: 10px;
  padding: var(--spacing-48) var(--spacing-30);

  div {
    display: flex;
    flex-direction: column;
  }

  .python-r-codes {
    &:nth-child(2) {
      margin-top: var(--spacing-40);
    }

    .codes-container {
      max-height: 240px;
      background: var(--main-light-shade-1);
      border: 0.89px solid rgba(0, 12, 31, 0.18);
      border-radius: 5px;
      padding: 15px var(--spacing-20);

      .codes {
        overflow: auto;
        background: #F8F8F8;
        scrollbar-color: (var(--main-light-shade-3) var(--main-light));
        scrollbar-width: thin;

        &::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        &::-webkit-scrollbar-thumb {
          background: var(--main-light-shade-3);
          border-radius: 10px;
        }

        pre {
          margin: 0;
        }

        .hljs {
          margin: -35px 0;
          font-size: 14px;
          overflow: unset;
        }

        .no-wrap {
          white-space: nowrap;
        }
      }
    }

    .img-text {
      display: flex;
      align-items: center;

      .h6 {
        margin: 0 0 0 10px;
        color: var(--accent-shade-2);
      }
    }

    .codes-info {
      margin: var(--spacing-12) 0;
    }

    button {
      font-family: Mazzard, sans-serif;
      margin-top: var(--spacing-16);
      align-self: flex-end;

      &:hover {
        color: var(--accent-shade-2);
      }

      &:active {
        color: var(--accent-shade-3);
      }

      &.copied {
        color: var(--accent-shade-3);
      }
    }
  }
}

@include bp($bp-laptop) {
  .interpretation-container {
    .interpretation-heading {
      font-size: 24px;
    }
  }

  .python-r-codes-container {
    .python-r-codes .codes-container {
      max-height: 200px;
    }
  }
}

@include bp($bp-mobile) {
  .calculator-container {
    margin-bottom: var(--spacing-100);

    .dropdowns-inputs {
      flex-direction: column;

      .decimal-places {
        width: 100%;
      }

      .inputs-tooltip .label-tooltip {
        .distribution .tooltip {
          left: 45px;

          &:after {
            left: 32.5%;
          }
        }

        .score .tooltip {
          left: 67px;

          &:after {
            left: 25%;
          }
        }

        .hypothesis .tooltip {
          left: 80%;

          &:after {
            left: 44.79%;
          }
        }

        .numerator .tooltip {
          left: -140%;

          &:after {
            left: 56.77%;
          }
        }

        .denominator .tooltip {
          left: -257%;

          &:after {
            left: 63.13%;
          }
        }
      }
    }
  }

  .python-r-codes-container {
    background: unset;
    border: unset;
    margin: 52px 0 0;

    .python-r-codes .codes-container {
      max-height: 240px;
    }
  }
}

@include bp($bp-phone) {
  .python-r-codes-container {
    .python-r-codes {
      .codes-container {
        border: unset;
      }

      .img-text {
        margin-bottom: var(--spacing-12);
      }

      .codes-info {
        margin-bottom: var(--spacing-24);
      }

      &.python {
        margin-bottom: var(--spacing-56);
      }

      button {
        margin: var(--spacing-24) auto 0 auto;
      }
    }
  }
}

@import '../../../../assets/sass/calculators/calculators.scss';
</style>
